import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Play, Pause, Info, BookOpen, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Wound images data with Dickies Held explanations - German labeled images
const woundData = {
  dekubitus: {
    title: "Dekubitus (Druckgeschwür)",
    description: "Dekubitus ist eine lokale Schädigung der Haut und des darunterliegenden Gewebes, die durch anhaltenden Druck oder Druck in Kombination mit Scherkräften entsteht.",
    slides: [
      {
        id: "dekubitus_grad_1",
        title: "Dekubitus Grad 1",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/4b3c9df709049144d0c0a31eb806b0730ec035f86ac7066c20ff52ffad297924.png",
        explanation: "Hier sehen Sie einen Dekubitus Grad 1. Das Hauptmerkmal ist eine nicht-wegdrückbare Rötung auf intakter Haut, typischerweise über einem Knochenvorsprung wie dem Kreuzbein oder der Ferse. Die Haut ist noch nicht verletzt, aber die Rötung bleibt bestehen, auch wenn der Druck entfernt wird. Wichtig: In diesem Stadium ist die Wunde noch reversibel! Sofortige Druckentlastung und regelmäßige Umlagerung sind entscheidend.",
        keyPoints: [
          "Nicht-wegdrückbare Rötung auf intakter Haut",
          "Über Knochenvorsprüngen lokalisiert",
          "Vollständig reversibel bei sofortiger Intervention",
          "Umlagerungsintervall sofort auf 2 Stunden reduzieren"
        ]
      },
      {
        id: "dekubitus_grad_2",
        title: "Dekubitus Grad 2",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/fdab483027453448fe56600cbde5a48c49ba82959e92835bdaa8b3b29ab399fb.png",
        explanation: "Dies ist ein Dekubitus Grad 2. Hier sehen Sie einen Teilverlust der Haut mit freiliegender Dermis. Das Wundbett ist rosa bis rot und feucht. Wichtig: Es kann auch als intakte oder rupturierte Blase erscheinen! In diesem Stadium sehen Sie keinen Belag oder Granulationsgewebe. Die Heilungschancen sind bei korrekter Behandlung noch sehr gut.",
        keyPoints: [
          "Teilverlust der Haut - Epidermis und Teile der Dermis betroffen",
          "Rosa bis rotes, feuchtes Wundbett",
          "Kann als intakte oder geplatzte Blase erscheinen",
          "Feuchte Wundbehandlung empfohlen (z.B. Hydrokolloid)"
        ]
      },
      {
        id: "dekubitus_grad_3",
        title: "Dekubitus Grad 3",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/7873db8e64211aceeee9baca92170e8bb3fd527055ac2c974331e6dae257083f.png",
        explanation: "Bei Grad 3 sehen wir einen Vollverlust der Haut. Das subkutane Fettgewebe ist sichtbar, aber Knochen, Sehnen und Muskeln liegen noch nicht frei. Sie können oft Belag im Wundbett sehen, aber dieser verdeckt nicht die Tiefe. Achten Sie auf die Wundränder - sie können unterminiert sein! Dies ist eine ernste Wunde, die professionelle Wundversorgung erfordert.",
        keyPoints: [
          "Vollverlust der Haut - alle Hautschichten betroffen",
          "Subkutanes Fettgewebe kann sichtbar sein",
          "Belag möglich, aber Tiefe erkennbar",
          "Oft unterminierte Wundränder - immer sondieren!"
        ]
      },
      {
        id: "dekubitus_grad_4",
        title: "Dekubitus Grad 4",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/52a8341adfa7d6263e948cf7b1465d9f7cfa5f4be03c4a343ac97543c9ca2d26.png",
        explanation: "Grad 4 ist die schwerste Form des Dekubitus. Hier liegt ein vollständiger Gewebeverlust vor mit freiliegenden Knochen, Sehnen oder Muskeln. Belag und Nekrose sind häufig vorhanden. Diese Wunden können tief unterminiert sein und Fistelgänge bilden. Wichtig: Diese Wunden erfordern interdisziplinäre Zusammenarbeit und oft chirurgische Intervention!",
        keyPoints: [
          "Vollständiger Gewebeverlust - tiefste Strukturen betroffen",
          "Knochen, Sehnen oder Muskeln können freiliegen",
          "Oft Nekrose und Belag vorhanden",
          "Chirurgisches Débridement oft erforderlich"
        ]
      }
    ]
  },
  ulcusCruris: {
    title: "Ulcus cruris (Unterschenkelgeschwür)",
    description: "Das Ulcus cruris ist eine chronische Wunde am Unterschenkel, die verschiedene Ursachen haben kann - am häufigsten venös bedingt.",
    slides: [
      {
        id: "ulcus_venosum",
        title: "Ulcus cruris venosum",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/c9af3962029ce4c0b2ed5e01180043f3e4e0224fb4d9994edba9108998b1b053.png",
        explanation: "Das venöse Unterschenkelgeschwür ist die häufigste Form des Ulcus cruris. Es entsteht durch chronische venöse Insuffizienz. Typische Merkmale sind: unregelmäßige Wundränder, flaches Wundbett, und die Lokalisation meist am Innenknöchel. Achten Sie auf die umgebende Haut - Sie sehen oft braune Pigmentierung, das sogenannte Hämosiderin-Staining, und Verhärtungen durch Lipodermatosklerose.",
        keyPoints: [
          "Häufigste Ursache: Chronische venöse Insuffizienz (CVI)",
          "Typische Lokalisation: Innenknöchel (Malleolus medialis)",
          "Umgebungshaut: Braune Pigmentierung, Stauungsekzem",
          "Therapie: Kompressionstherapie ist das A und O!"
        ]
      },
      {
        id: "ulcus_arteriosum",
        title: "Ulcus cruris arteriosum",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/f6a2f847c4e5bc717542ebe2fc61ac69340e8c5d894ab3bfd8fd4fc3181d9432.png",
        explanation: "Das arterielle Unterschenkelgeschwür entsteht durch periphere arterielle Verschlusskrankheit. Es sieht ganz anders aus als das venöse Ulcus! Die Wundränder sind scharf begrenzt, wie ausgestanzt. Das Wundbett ist blass und trocken. Die umgebende Haut ist dünn und glänzend. Wichtig: Der Fuß ist oft kalt und blass! Vor jeder Kompression muss der Gefäßstatus geprüft werden - der ABI (Knöchel-Arm-Index) ist essentiell!",
        keyPoints: [
          "Ursache: Periphere arterielle Verschlusskrankheit (pAVK)",
          "Lokalisation: Oft an Zehen, Ferse, Außenknöchel",
          "Wundränder: Scharf begrenzt, 'ausgestanzt'",
          "WICHTIG: Keine Kompression ohne Gefäßdiagnostik (ABI)!"
        ]
      }
    ]
  },
  diabetischerFuss: {
    title: "Diabetisches Fußsyndrom (DFS)",
    description: "Das Diabetische Fußsyndrom ist eine schwerwiegende Komplikation des Diabetes mellitus und Hauptursache für nicht-traumatische Amputationen.",
    slides: [
      {
        id: "dfs_neuropathisch",
        title: "Diabetisches Fußulkus",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/d3c1a9697fb771941072aaa1b598abb3b780e3d192cf942b3a171efab0ab808b.png",
        explanation: "Das diabetische Fußulkus entsteht durch die Kombination von Neuropathie und Angiopathie. Hier sehen Sie ein typisches neuropathisches Ulkus an einer Druckstelle. Beachten Sie den Kallus am Wundrand - ein Warnsignal! Die Patienten spüren oft keine Schmerzen, was die Wunde besonders gefährlich macht. In Deutschland führt das DFS jährlich zu etwa 50.000 Amputationen - viele davon vermeidbar!",
        keyPoints: [
          "Entstehung: Neuropathie + Angiopathie + mechanische Belastung",
          "Lokalisation: Druckpunkte - Großzehenballen, Ferse, Kleinzehe",
          "Warnsignal: Kallus am Wundrand",
          "Therapie: Druckentlastung + Blutzuckereinstellung + Wundversorgung"
        ]
      }
    ]
  }
};

// Module-specific themed content - each module has its own relevant images
const moduleThemedContent = {
  2: {
    title: "Rechtliche Grundlagen & Haftung",
    description: "Wichtige rechtliche Aspekte im Wundmanagement",
    slides: [
      {
        id: "recht_1",
        title: "Rechtliche Dokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/51f32cf221af80c83b3c221b3295e4c82d8bfa1fa46054770b269f94150edb0b.png",
        explanation: "Die rechtliche Absicherung im Wundmanagement beginnt mit der korrekten Dokumentation. Das Bild zeigt die wichtigsten Elemente: Gesetzestexte wie § 630f BGB zur Dokumentationspflicht, die Waage der Justitia für faire Beurteilung, und die Patientenakte. Merke: Was nicht dokumentiert ist, hat rechtlich nicht stattgefunden! Die Dokumentation ist Ihr wichtigstes Beweismittel bei Haftungsfragen.",
        keyPoints: [
          "§ 630f BGB: Dokumentationspflicht für alle Behandlungen",
          "§ 630h BGB: Beweislastumkehr bei Dokumentationsmängeln",
          "10 Jahre Aufbewahrungspflicht nach Behandlungsende",
          "Objektive, zeitnahe und vollständige Einträge"
        ]
      },
      {
        id: "recht_2",
        title: "Haftungsrisiken vermeiden",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/51f32cf221af80c83b3c221b3295e4c82d8bfa1fa46054770b269f94150edb0b.png",
        explanation: "Als Leitungskraft tragen Sie besondere Verantwortung. Der Expertenstandard 'Pflege von Menschen mit chronischen Wunden' ist der fachliche Maßstab vor Gericht. Bei Abweichungen müssen Sie begründen können, warum. Organisieren Sie regelmäßige Schulungen und dokumentieren Sie diese - so weisen Sie nach, dass Ihr Team qualifiziert ist.",
        keyPoints: [
          "Expertenstandard als rechtlicher Maßstab",
          "Organisationsverschulden bei mangelnder Einweisung",
          "Schulungsnachweise als Absicherung",
          "Delegation nur an qualifiziertes Personal"
        ]
      }
    ],
    intro: "In diesem Modul zeige ich dir, wie du dich und deine Einrichtung rechtlich absicherst. Die Dokumentation ist dabei dein wichtigstes Werkzeug!"
  },
  3: {
    title: "Verantwortlichkeiten & Delegation",
    description: "Klare Strukturen für effektives Wundmanagement",
    slides: [
      {
        id: "team_1",
        title: "Teamstruktur & RACI-Modell",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/d9fceff5a9150c17f34552ed5083967cbc4e82a3c256a6f69d97d809e60400a0.png",
        explanation: "Das RACI-Modell hilft dir, Verantwortlichkeiten klar zu definieren: R steht für Responsible - wer führt aus. A für Accountable - wer ist verantwortlich. C für Consulted - wer wird gefragt. I für Informed - wer wird informiert. Als Leitungskraft bist du meist Accountable, während deine Pflegekräfte Responsible sind. Klare Strukturen verhindern, dass Aufgaben 'durchfallen'!",
        keyPoints: [
          "R = Responsible: Führt die Aufgabe aus",
          "A = Accountable: Trägt die Verantwortung",
          "C = Consulted: Wird um Rat gefragt",
          "I = Informed: Wird über Ergebnisse informiert"
        ]
      },
      {
        id: "team_2",
        title: "Delegation richtig gemacht",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/d9fceff5a9150c17f34552ed5083967cbc4e82a3c256a6f69d97d809e60400a0.png",
        explanation: "Bei der Delegation im Wundmanagement gelten besondere Regeln. Du darfst nur an qualifiziertes Personal delegieren und musst die Qualifikation vorher prüfen. Die Anordnungsverantwortung bleibt bei dir! Erstelle klare Stellenbeschreibungen und Kompetenzprofile. So weiß jeder im Team genau, was er darf und was nicht.",
        keyPoints: [
          "Qualifikation vor Delegation prüfen",
          "Anordnungsverantwortung bleibt bei der Leitung",
          "Klare Stellenbeschreibungen erstellen",
          "Regelmäßige Kompetenzüberprüfung"
        ]
      }
    ],
    intro: "Klare Verantwortlichkeiten sind das Fundament eines funktionierenden Wundmanagements. Hier zeige ich dir, wie du dein Team optimal strukturierst!"
  },
  4: {
    title: "Qualitätsmanagement",
    description: "Qualitätssicherung und MD-Prüfung",
    slides: [
      {
        id: "qm_1",
        title: "MD-Prüfung bestehen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/2cb123e7f3bc9dd4b9b1b41f8e042aba67e1076194f3e57be09734c10a5c4194.png",
        explanation: "Die MD-Prüfung (früher MDK) prüft die Qualität deiner Wundversorgung. Das Bild zeigt die wichtigsten Elemente: Audit-Checklisten, Qualitätssiegel und strukturierte Dokumentation. Einrichtungen mit regelmäßigen internen Audits haben 30% höhere Erfolgsquoten! Bereite dich vor, indem du die Prüfkriterien kennst und regelmäßig selbst prüfst.",
        keyPoints: [
          "Interne Audits erhöhen Erfolgsquote um 30%",
          "Prüfkriterien vorher kennen und umsetzen",
          "Lückenlose Dokumentation ist entscheidend",
          "Wundverlauf nachvollziehbar darstellen"
        ]
      },
      {
        id: "qm_2",
        title: "Qualitätszirkel etablieren",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/2cb123e7f3bc9dd4b9b1b41f8e042aba67e1076194f3e57be09734c10a5c4194.png",
        explanation: "Ein Qualitätszirkel Wundmanagement trifft sich regelmäßig, um Fälle zu besprechen und voneinander zu lernen. Nutze Kennzahlen wie Heilungsraten, Komplikationen und Materialverbrauch. PDCA-Zyklus anwenden: Plan - Do - Check - Act. So entwickelst du dein Wundmanagement kontinuierlich weiter!",
        keyPoints: [
          "Regelmäßige Fallbesprechungen im Team",
          "Kennzahlen definieren und tracken",
          "PDCA-Zyklus für kontinuierliche Verbesserung",
          "Best Practices dokumentieren und teilen"
        ]
      }
    ],
    intro: "Qualitätsmanagement ist kein Papiertiger - es macht deine Arbeit besser und sicherer. Hier zeige ich dir die wichtigsten Werkzeuge!"
  },
  5: {
    title: "Wirtschaftlichkeit",
    description: "Kosten verstehen und optimieren",
    slides: [
      {
        id: "wirtschaft_1",
        title: "Kostenstruktur verstehen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/b1cdcf3b8bc0a246ff7fa65c2ab513b35c0830eb7a4ae0d787e4566faf74d0c1.png",
        explanation: "Eine chronische Wunde kostet durchschnittlich 8.000-9.500 Euro pro Jahr! Das Bild zeigt die Kostenverteilung: Materialkosten, Personalkosten und indirekte Kosten. Aber: Qualität spart langfristig Geld! Eine optimal versorgte Wunde heilt schneller und verursacht weniger Komplikationen. Investiere in gute Materialien und Schulungen.",
        keyPoints: [
          "8.000-9.500 € durchschnittliche Jahreskosten pro Wunde",
          "Materialkosten nur 20-30% der Gesamtkosten",
          "Personalkosten größter Kostenfaktor",
          "Prävention ist immer günstiger als Behandlung"
        ]
      },
      {
        id: "wirtschaft_2",
        title: "Budget optimal einsetzen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/b1cdcf3b8bc0a246ff7fa65c2ab513b35c0830eb7a4ae0d787e4566faf74d0c1.png",
        explanation: "Smart einkaufen heißt nicht billig einkaufen! Moderne Wundauflagen sind teurer, können aber die Verbandswechsel-Frequenz halbieren. Das spart Personalzeit und verbessert die Heilung. Erstelle eine Kosten-Nutzen-Analyse für deine häufigsten Wundtypen. Nach etwa 9 Monaten erreicht ein strukturiertes Wundmanagement den Break-even.",
        keyPoints: [
          "Moderne Auflagen: weniger Verbandswechsel = weniger Personalkosten",
          "Break-even nach ca. 9 Monaten",
          "Kosten-Nutzen-Analyse pro Wundtyp erstellen",
          "Rahmenverträge mit Lieferanten verhandeln"
        ]
      }
    ],
    intro: "Gutes Wundmanagement ist wirtschaftlich! Hier zeige ich dir, wie du Qualität und Kosten in Einklang bringst."
  },
  6: {
    title: "Führungsaufgaben",
    description: "Team führen und entwickeln",
    slides: [
      {
        id: "fuehrung_1",
        title: "Team schulen und motivieren",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/4bde9b7b5625fef1f88fea69cd7f2e84692c0cd47558d93d1b0517c99842b0c1.png",
        explanation: "Als Führungskraft bist du Coach und Mentor. Das Bild zeigt eine typische Schulungssituation: Praxisnahes Lernen im Team. Plane regelmäßige Fortbildungen - mindestens 2x jährlich. Nutze Fallbesprechungen als Lernchance. Teams mit offener Fehlerkultur haben bis zu 30% weniger Komplikationen!",
        keyPoints: [
          "Mindestens 2 Schulungen pro Jahr",
          "Praxisnahe Fallbesprechungen",
          "Offene Fehlerkultur etablieren",
          "30% weniger Komplikationen bei guter Teamkultur"
        ]
      },
      {
        id: "fuehrung_2",
        title: "Wundexperten entwickeln",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/4bde9b7b5625fef1f88fea69cd7f2e84692c0cd47558d93d1b0517c99842b0c1.png",
        explanation: "Fördere Wundexperten in deinem Team! Diese können als Multiplikatoren wirken und Kollegen anleiten. Qualifizierte Wundexperten sind Gold wert. Unterstütze Weiterbildungen und schaffe Karrierepfade. Ein starkes Team macht deine Arbeit als Leitung leichter!",
        keyPoints: [
          "Wundexperten als Multiplikatoren einsetzen",
          "Zertifizierte Weiterbildungen fördern",
          "Karrierepfade im Wundmanagement aufzeigen",
          "Mentoring-Programme etablieren"
        ]
      }
    ],
    intro: "Dein Team ist dein wichtigstes Asset! Hier zeige ich dir, wie du es optimal führst und entwickelst."
  },
  7: {
    title: "Dokumentation & IT",
    description: "Digitale Systeme und Datenschutz",
    slides: [
      {
        id: "doku_1",
        title: "Digitale Wunddokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/02cc84105ffe478e91cbc0c886852f224b5df535ba6c9e69512693946013294f.png",
        explanation: "Moderne Wunddokumentation ist digital! Das Bild zeigt ein Tablet mit Wunddokumentation, geschützt durch DSGVO-konforme Systeme. Digitale Fotos ermöglichen objektive Verlaufskontrollen. Wichtig: Niemals Wundfotos auf privaten Smartphones speichern - das verstößt gegen die DSGVO! Nutze nur freigegebene Geräte und Systeme.",
        keyPoints: [
          "Digitale Fotos für objektive Verlaufskontrolle",
          "NIEMALS private Smartphones für Wundfotos!",
          "DSGVO-konforme Systeme verwenden",
          "Einwilligung des Patienten dokumentieren"
        ]
      },
      {
        id: "doku_2",
        title: "Datenschutz im Alltag",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/02cc84105ffe478e91cbc0c886852f224b5df535ba6c9e69512693946013294f.png",
        explanation: "Datenschutz ist kein Hindernis, sondern Patientenschutz! Stelle sicher, dass nur berechtigte Personen Zugriff auf Wunddaten haben. Verschlüsselte Übertragung bei digitalen Systemen ist Pflicht. Schule dein Team regelmäßig zum Thema Datenschutz - Verstöße können teuer werden!",
        keyPoints: [
          "Zugriffsrechte klar definieren",
          "Verschlüsselte Datenübertragung",
          "Regelmäßige Datenschutz-Schulungen",
          "Verstöße: Bußgelder bis 20 Mio. € möglich"
        ]
      }
    ],
    intro: "Dokumentation ist Pflicht - aber sie kann auch Spaß machen! Moderne digitale Tools machen die Arbeit einfacher und sicherer."
  },
  8: {
    title: "Gesamtkonzept",
    description: "Alles zusammenbringen",
    slides: [
      {
        id: "konzept_1",
        title: "Implementierungs-Roadmap",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/3dd5ad1796e5de99119a05542ee4daf18693ef44d9bf8452a161d91daa6ae8e3.png",
        explanation: "Jetzt bringst du alles zusammen! Das Bild zeigt den Weg zum Erfolg: Von der Analyse über die Implementierung bis zur kontinuierlichen Verbesserung. Starte mit einer Ist-Analyse, definiere Ziele und plane Meilensteine. In 9-12 Monaten kannst du ein vollständiges Wundmanagement-System aufbauen!",
        keyPoints: [
          "Phase 1: Ist-Analyse (1-2 Monate)",
          "Phase 2: Konzeptentwicklung (2-3 Monate)",
          "Phase 3: Implementierung (3-4 Monate)",
          "Phase 4: Evaluation & Optimierung (fortlaufend)"
        ]
      },
      {
        id: "konzept_2",
        title: "Erfolg messen und feiern",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/3dd5ad1796e5de99119a05542ee4daf18693ef44d9bf8452a161d91daa6ae8e3.png",
        explanation: "Erfolge machen Spaß - und motivieren das Team! Definiere messbare Ziele: Heilungsraten, Komplikationsraten, Patientenzufriedenheit. Feiere erreichte Meilensteine mit dem Team. Nach diesem Kurs bist du bereit, ein exzellentes Wundmanagement aufzubauen. Du schaffst das!",
        keyPoints: [
          "KPIs definieren: Heilungsrate, Komplikationen, Zufriedenheit",
          "Regelmäßiges Reporting etablieren",
          "Erfolge im Team feiern",
          "Kontinuierliche Verbesserung leben"
        ]
      }
    ],
    intro: "Du hast es fast geschafft! Jetzt bringen wir alles zusammen zu deinem persönlichen Erfolgskonzept."
  }
};

// Module-specific wound content (for Module 1 - keep wound images)
const getModuleWoundContent = (moduleOrder) => {
  // For modules 2-8, use themed content
  if (moduleOrder >= 2 && moduleThemedContent[moduleOrder]) {
    return {
      useThemedContent: true,
      themedData: moduleThemedContent[moduleOrder]
    };
  }
  
  // Module 1 keeps wound images
  const moduleContent = {
    1: {
      categories: ['dekubitus', 'ulcusCruris', 'diabetischerFuss'],
      intro: "In diesem Modul zeige ich dir die wichtigsten Wundarten, die du als Leitungskraft kennen musst. Klicke dich durch die Bilder und ich erkläre dir alles!",
      useThemedContent: false
    }
  };
  return moduleContent[moduleOrder] || moduleContent[1];
};

const WoundImageExplanation = ({ moduleOrder, authHeader = {}, onComplete }) => {
  const [currentCategory, setCurrentCategory] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showKeyPoints, setShowKeyPoints] = useState(false);
  const audioRef = useRef(null);

  const moduleContent = getModuleWoundContent(moduleOrder);
  const categories = moduleContent.categories.map(cat => woundData[cat]);
  const category = categories[currentCategory];
  const slide = category?.slides[currentSlide];

  useEffect(() => {
    // Reset slide when category changes
    setCurrentSlide(0);
    setShowKeyPoints(false);
  }, [currentCategory]);

  const speakText = async (text) => {
    if (isMuted || !text) return;
    
    setIsSpeaking(true);
    try {
      const response = await axios.post(
        `${API}/tts/base64`,
        { text, voice: "nova" },
        authHeader
      );
      
      const audioSrc = `data:audio/mpeg;base64,${response.data.audio}`;
      if (audioRef.current) {
        audioRef.current.src = audioSrc;
        await audioRef.current.play();
        
        audioRef.current.onended = () => {
          setIsSpeaking(false);
        };
      }
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
    }
  };

  const startExplanation = () => {
    setIsPlaying(true);
    if (slide) {
      speakText(slide.explanation);
    }
  };

  const nextSlide = () => {
    setShowKeyPoints(false);
    if (currentSlide < category.slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      if (isPlaying && category.slides[currentSlide + 1]) {
        setTimeout(() => speakText(category.slides[currentSlide + 1].explanation), 500);
      }
    } else if (currentCategory < categories.length - 1) {
      setCurrentCategory(currentCategory + 1);
      setCurrentSlide(0);
    } else if (onComplete) {
      onComplete();
    }
  };

  const prevSlide = () => {
    setShowKeyPoints(false);
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (currentCategory > 0) {
      setCurrentCategory(currentCategory - 1);
      setCurrentSlide(categories[currentCategory - 1]?.slides.length - 1 || 0);
    }
  };

  const totalSlides = categories.reduce((sum, cat) => sum + cat.slides.length, 0);
  const currentTotalSlide = categories.slice(0, currentCategory).reduce((sum, cat) => sum + cat.slides.length, 0) + currentSlide + 1;

  if (!category || !slide) {
    return (
      <div className="bg-cream-100 rounded-2xl p-8 border border-cream-300 text-center">
        <p className="font-display italic text-sand-600">Bilder werden geladen …</p>
      </div>
    );
  }

  return (
    <div className="bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden" data-testid="wound-image-explanation">
      {/* Header */}
      <div className="px-6 py-4 border-b border-cream-300">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase font-semibold mb-1" style={{ letterSpacing: '0.24em', color: '#8F650F' }}>
              Bild {currentTotalSlide} von {totalSlides} · {category.title}
            </p>
            <p className="font-display italic text-[15px] text-espresso">
              Lerne die wichtigsten chronischen Wundarten zu erkennen.
            </p>
          </div>
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-2.5 rounded-full transition-colors flex-shrink-0"
            style={{ border: '1px solid rgba(216,146,50,0.6)', color: isMuted ? '#E07A5F' : '#D89232' }}
          >
            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Category Tabs — Rubriken */}
      <div className="px-6 py-3 border-b border-cream-300 flex items-center gap-x-1 overflow-x-auto">
        {categories.map((cat, idx) => (
          <React.Fragment key={cat.title}>
            {idx > 0 && <span className="text-[10px] px-1.5" style={{ color: '#8F650F' }}>·</span>}
            <button
              onClick={() => {
                setCurrentCategory(idx);
                setCurrentSlide(0);
              }}
              className="text-[11px] uppercase font-semibold whitespace-nowrap pb-0.5 transition-colors"
              style={{
                letterSpacing: '0.16em',
                color: idx === currentCategory ? '#E07A5F' : '#A89A88',
                borderBottom: idx === currentCategory ? '2px solid #E07A5F' : '2px solid transparent',
              }}
            >
              {cat.title}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row">
        {/* Left - Image im Espresso-Passepartout */}
        <div className="flex-1 lg:w-2/3 p-6">
          <div className="relative rounded-xl overflow-hidden p-4" style={{ backgroundColor: '#23211E', border: '1px solid rgba(216,146,50,0.4)' }}>
            {!isPlaying && (
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer group z-10"
                style={{ backgroundColor: 'rgba(27,26,24,0.6)' }}
                onClick={startExplanation}
              >
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform mx-auto mb-4" style={{ backgroundColor: '#E07A5F' }}>
                    <Play className="w-9 h-9 ml-1" style={{ color: '#F5F0EA' }} fill="#F5F0EA" />
                  </div>
                  <p className="font-mono text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: '#F5F0EA' }}>Erklärung anhören</p>
                </div>
              </div>
            )}
            <img 
              src={slide.image} 
              alt={slide.title}
              className="w-full h-auto rounded-md"
            />
          </div>
          
          {/* Image Title */}
          <div className="mt-4 text-center">
            <h4 className="font-display text-xl text-espresso">{slide.title}</h4>
            <p className="font-display italic text-sm text-sand-600 mt-1">{category.description}</p>
          </div>
        </div>

        {/* Right — Die Dozentin erklärt */}
        <div className="lg:w-1/3 border-l border-cream-300 flex flex-col">
          {/* Explanation */}
          <div className="flex-1 p-5 overflow-y-auto">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full flex items-center justify-center font-display text-[15px] flex-shrink-0" style={{ backgroundColor: '#E07A5F', color: '#F5F0EA' }}>DH</span>
              <span className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>Die Dozentin erklärt</span>
            </div>
            <div className="pl-4" style={{ borderLeft: '2px solid #D89232' }}>
              <p className={`text-sand-800 leading-relaxed text-sm ${isSpeaking ? 'animate-pulse' : ''}`}>
                {isPlaying ? slide.explanation : moduleContent.intro}
              </p>
            </div>

            {/* Key Points */}
            {isPlaying && (
              <div className="mt-5">
                <button
                  onClick={() => setShowKeyPoints(!showKeyPoints)}
                  className="w-full flex items-center justify-between py-2 border-b text-sand-600 hover:text-terra transition-colors"
                  style={{ borderColor: '#E0D6CA' }}
                >
                  <span className="font-mono text-[10.5px] uppercase font-semibold" style={{ letterSpacing: '0.18em' }}>
                    Kernpunkte anzeigen
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showKeyPoints ? 'rotate-90' : ''}`} />
                </button>
                
                {showKeyPoints && (
                  <div className="mt-3">
                    {slide.keyPoints.map((point, idx) => (
                      <div key={idx} className="flex items-start gap-3 py-2.5 border-b" style={{ borderColor: '#E0D6CA' }}>
                        <span className="font-display text-[17px] text-terra leading-none flex-shrink-0 pt-0.5">{idx + 1}</span>
                        <p className="text-sm text-sand-700 leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Replay Button — Gold-Outline-Kreis */}
            {isPlaying && (
              <button
                onClick={() => speakText(slide.explanation)}
                disabled={isSpeaking}
                className="mt-5 inline-flex items-center gap-2.5 disabled:opacity-50 transition-colors group"
              >
                <span className="w-10 h-10 rounded-full flex items-center justify-center transition-colors" style={{ border: '1px solid rgba(216,146,50,0.6)' }}>
                  <Volume2 className="w-4 h-4" style={{ color: '#8F650F' }} />
                </span>
                <span className="font-mono text-[10.5px] uppercase font-semibold text-sand-600 group-hover:text-terra" style={{ letterSpacing: '0.16em' }}>
                  {isSpeaking ? 'Spricht …' : 'Erklärung wiederholen'}
                </span>
              </button>
            )}
          </div>

          {/* Slide Dots */}
          <div className="p-4 border-t border-cream-300">
            <div className="flex items-center justify-center gap-2 mb-2.5">
              {category.slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentSlide(idx);
                    setShowKeyPoints(false);
                    if (isPlaying) {
                      setTimeout(() => speakText(category.slides[idx].explanation), 300);
                    }
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    idx === currentSlide ? 'bg-terra' : 'bg-cream-300'
                  }`}
                />
              ))}
            </div>
            <p className="font-mono text-[9.5px] uppercase text-sand-500 text-center" style={{ letterSpacing: '0.16em' }}>
              {category.title}: Bild {currentSlide + 1} von {category.slides.length}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="px-6 py-4 border-t border-cream-300 flex items-center justify-between">
        <button
          onClick={prevSlide}
          disabled={currentCategory === 0 && currentSlide === 0}
          className="group flex items-center gap-1.5 text-[13px] font-semibold text-sand-500 hover:text-terra disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          Zurück
        </button>

        <div className="flex items-center gap-1">
          {Array.from({ length: totalSlides }).map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx < currentTotalSlide ? 'bg-[#D89232]' : 'bg-cream-300'
              }`}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          className="h-10 px-7 rounded-full bg-transparent text-[12px] font-bold uppercase inline-flex items-center gap-2 transition-colors"
          style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.16em' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
        >
          {currentCategory === categories.length - 1 && currentSlide === category.slides.length - 1 
            ? 'Abschließen' 
            : 'Weiter'} →
        </button>
      </div>

      {/* Hinweis als kursive Fußnote */}
      <div className="px-6 pb-5">
        <p className="font-display italic text-[13px] text-sand-600">
          <span style={{ color: '#8F650F' }}>✳</span> Diese Bilder dienen zu Schulungszwecken — die tatsächliche Klassifikation einer Wunde gehört immer in die Hände von geschultem Fachpersonal.
        </p>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} />
    </div>
  );
};

export default WoundImageExplanation;
